import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Snackbar,
  LinearProgress,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AdminPanelSettings as AdminIcon,
  PersonAdd as PersonAddIcon,
  CloudDownload as ImportIcon,
  Person as PersonIcon,
  ChildCare as ChildIcon,
  Email as EmailIcon,
  RemoveRedEye as ImpersonateIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Sports as CoachIcon,
  FamilyRestroom as ParentIcon,
  IceSkating as SkaterIcon,
} from '@mui/icons-material';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { useImpersonation } from '../context/ImpersonationContext';
import { API_BASE_URL, REGISTRATION_BASE_URL } from '../config/environment';
import registrationService from '../services/registrationService';

// User roles and permissions
const USER_ROLES = {
  PARENT: 'PARENT',
  ADMIN: 'ADMIN',
  SUPER_ADMIN: 'SUPER_ADMIN',
  INTERCLUB_ADMIN: 'INTERCLUB_ADMIN'
};

const USER_PERMISSIONS = {
  MANAGE_COMPETITIONS: 'manage_competitions',
  MANAGE_USERS: 'manage_users',
  MANAGE_RESULTS: 'manage_results',
  VIEW_ADMIN_PANEL: 'view_admin_panel',
  MANAGE_ADMINS: 'manage_admins'
};

export default function UserManagement() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();
  const { user: currentUser, isAdmin, isSuperAdmin, getIdToken } = useFirebaseAuth();
  const { startImpersonation } = useImpersonation();
  
  // State management
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Initialize search from URL query parameter
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const sortField = 'lastName';
  const sortDirection = 'asc';
  
  // Firebase users state
  const [firebaseUsers, setFirebaseUsers] = useState([]);
  const [firebaseUsersLoading, setFirebaseUsersLoading] = useState(false);
  
  // Legacy users state
  const [legacyUsers, setLegacyUsers] = useState([]);
  const [legacyUsersLoading, setLegacyUsersLoading] = useState(false);
  
  // Admin users state
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [interclubs, setInterclubs] = useState([]);
  
  // Dialog states
  const [userDetailOpen, setUserDetailOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [impersonateDialogOpen, setImpersonateDialogOpen] = useState(false);
  const [userToImpersonate, setUserToImpersonate] = useState(null);
  const [impersonating, setImpersonating] = useState(false);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Load Firebase users
  const loadFirebaseUsers = useCallback(async () => {
    try {
      setFirebaseUsersLoading(true);
      
      const token = await getIdToken();
      if (!token) {
        throw new Error('Unable to get authentication token');
      }
      
      const response = await fetch(`${API_BASE_URL}/firebase-users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch Firebase users');
      }
      
      const users = await response.json();
      setFirebaseUsers(users);
    } catch (err) {
      console.error('Error loading Firebase users:', err);
      setFirebaseUsers([]);
    } finally {
      setFirebaseUsersLoading(false);
    }
  }, [getIdToken]);

  // Load legacy users from Google Sheets
  const loadLegacyUsers = useCallback(async () => {
    try {
      setLegacyUsersLoading(true);
      const response = await fetch(`${API_BASE_URL}/users`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch legacy users');
      }
      
      const users = await response.json();
      setLegacyUsers(users);
    } catch (err) {
      console.error('Error loading legacy users:', err);
      setLegacyUsers([]);
    } finally {
      setLegacyUsersLoading(false);
    }
  }, []);

  // Load admin users
  const loadAdminUsers = useCallback(async () => {
    try {
      setAdminUsersLoading(true);
      
      const token = await getIdToken();
      if (!token) {
        throw new Error('Unable to get authentication token');
      }
      
      const response = await fetch(`${API_BASE_URL}/admin-users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch admin users');
      }
      
      const users = await response.json();
      setAdminUsers(users);
    } catch (err) {
      console.error('Error loading admin users:', err);
      setAdminUsers([]);
    } finally {
      setAdminUsersLoading(false);
    }
  }, [getIdToken]);

  const loadInterclubs = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/interclubs`);
      if (!response.ok) {
        throw new Error('Failed to fetch interclubs');
      }
      const data = await response.json();
      setInterclubs(data || []);
    } catch (err) {
      console.error('Error loading interclubs:', err);
      setInterclubs([]);
    }
  }, []);

  // Load all user data
  const loadAllData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load data in parallel
      await Promise.all([
        loadFirebaseUsers(),
        loadLegacyUsers(),
        loadAdminUsers(),
        loadInterclubs()
      ]);
    } catch (err) {
      setError('Failed to load user data');
      console.error('Error loading user data:', err);
    } finally {
      setLoading(false);
    }
  }, [loadFirebaseUsers, loadLegacyUsers, loadAdminUsers, loadInterclubs]);

  // Load all data on component mount
  useEffect(() => {
    console.log('🔄 UserManagement useEffect triggered');
    console.log('👤 currentUser:', currentUser);
    console.log('🔑 isAdmin():', isAdmin());
    
    if (isAdmin()) {
      console.log('✅ User is admin, loading data...');
      loadAllData();
    } else {
      console.log('❌ User is not admin or admin status not loaded');
    }
  }, [isAdmin, currentUser, loadAllData]);

  // Create new Firebase user
  const createFirebaseUser = async (userData) => {
    try {
      const token = await getIdToken();
      if (!token) {
        throw new Error('Unable to get authentication token');
      }
      
      const response = await fetch(`${API_BASE_URL}/firebase-users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to create user');
      }
      
      const newUser = await response.json();
      setFirebaseUsers(prev => [...prev, newUser]);
      showSnackbar('User created successfully', 'success');
      return newUser;
    } catch (err) {
      console.error('Error creating user:', err);
      showSnackbar(`Failed to create user: ${err.message}`, 'error');
      throw err;
    }
  };

  // Update user permissions
  const updateUserPermissions = async (userId, permissions) => {
    try {
      const token = await getIdToken();
      if (!token) {
        throw new Error('Unable to get authentication token');
      }
      
      const response = await fetch(`${API_BASE_URL}/admin-users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ permissions })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update user permissions');
      }
      
      // Update local state
      setAdminUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, permissions } : user
      ));
      
      showSnackbar('User permissions updated successfully', 'success');
    } catch (err) {
      console.error('Error updating user permissions:', err);
      showSnackbar(`Failed to update permissions: ${err.message}`, 'error');
    }
  };

  // Update admin user (role, permissions, etc.)
  const updateAdminUser = async (userId, userData) => {
    try {
      const token = await getIdToken();
      if (!token) {
        throw new Error('Unable to get authentication token');
      }
      
      console.log('🔄 Updating admin user:', userId, 'with data:', userData);
      
      const response = await fetch(`${API_BASE_URL}/admin-users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      const updatedUser = await response.json();
      console.log('✅ Admin user updated successfully:', updatedUser);
      
      // Update local state
      setAdminUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, ...updatedUser } : user
      ));
      
      showSnackbar('User updated successfully', 'success');
      return updatedUser;
    } catch (err) {
      console.error('❌ Error updating admin user:', err);
      showSnackbar(`Failed to update user: ${err.message}`, 'error');
      throw err;
    }
  };

  // Delete a user (admin function)
  const deleteUser = async (user) => {
    const uid = user.uid || user.id;
    const displayName = user.displayName || user.email;
    
    if (!window.confirm(`Are you sure you want to delete user "${displayName}"? This will disable their account and cannot be easily undone.`)) {
      return;
    }
    
    try {
      const token = await getIdToken();
      if (!token) {
        throw new Error('Unable to get authentication token');
      }
      
      const response = await fetch(`${API_BASE_URL}/admin/users/${uid}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete user');
      }
      
      showSnackbar(`User "${displayName}" deleted successfully`, 'success');
      
      // Reload the appropriate list
      await loadAllData();
    } catch (err) {
      console.error('Error deleting user:', err);
      showSnackbar(`Failed to delete user: ${err.message}`, 'error');
    }
  };

  // Import legacy users to Firebase
  const importLegacyUsers = async () => {
    try {
      setImportProgress(0);
      
      // Check if user is available
      if (!currentUser) {
        throw new Error('No authenticated user available');
      }
      
      console.log('🔑 Getting ID token for user:', currentUser.email);
      console.log('👤 currentUser object:', currentUser);
      console.log('🔑 currentUser.getIdToken type:', typeof currentUser.getIdToken);
      console.log('🔑 currentUser methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(currentUser)));
      
      // Try multiple methods to get the token
      let token;
      
      // Method 1: Direct from user object
      try {
        console.log('🔑 Method 1: Trying currentUser.getIdToken()...');
        token = await currentUser.getIdToken();
        console.log('✅ Method 1 succeeded');
      } catch (tokenError) {
        console.log('⚠️ Method 1 failed:', tokenError.message);
      }
      
      // Method 2: Context function
      if (!token) {
        try {
          console.log('🔑 Method 2: Trying context getIdToken()...');
          token = await getIdToken();
          console.log('✅ Method 2 succeeded');
        } catch (contextError) {
          console.log('⚠️ Method 2 failed:', contextError.message);
        }
      }
      
      // Method 3: Force refresh token
      if (!token) {
        try {
          console.log('🔑 Method 3: Trying force refresh...');
          token = await currentUser.getIdToken(true); // Force refresh
          console.log('✅ Method 3 succeeded');
        } catch (refreshError) {
          console.log('⚠️ Method 3 failed:', refreshError.message);
        }
      }
      
      if (!token) {
        console.log('❌ All token methods failed');
        throw new Error('Unable to get authentication token. Please try signing out and back in.');
      }
      
      console.log('🔑 Token obtained successfully');
      
      const response = await fetch(`${API_BASE_URL}/import-legacy-users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to import legacy users');
      }
      
      const result = await response.json();
      showSnackbar(`Successfully imported ${result.importedCount} users`, 'success');
      
      // Reload data
      await loadAllData();
    } catch (err) {
      console.error('Error importing legacy users:', err);
      showSnackbar(`Failed to import users: ${err.message}`, 'error');
    } finally {
      setImportProgress(0);
    }
  };

  // Match users with children
  const matchUsersWithChildren = () => {
    const userMap = new Map();
    
    // Create map of parent emails to their data
    legacyUsers.forEach(user => {
      if (user.parentEmail) {
        if (!userMap.has(user.parentEmail)) {
          userMap.set(user.parentEmail, {
            parent: null,
            children: []
          });
        }
        userMap.get(user.parentEmail).children.push(user);
      }
    });
    
    // Match with Firebase users
    firebaseUsers.forEach(firebaseUser => {
      if (userMap.has(firebaseUser.email)) {
        userMap.get(firebaseUser.email).parent = firebaseUser;
      }
    });
    
    return Array.from(userMap.entries()).map(([email, data]) => ({
      email,
      parent: data.parent,
      children: data.children
    }));
  };

  // Get sorted users based on active tab
  const getSortedUsers = () => {
    let users = [];
    
    switch (activeTab) {
      case 0: // Firebase Users
        users = firebaseUsers;
        break;
      case 1: // Legacy Users
        users = legacyUsers;
        break;
      case 2: // Admin Users
        users = adminUsers;
        break;
      case 3: // User Matching
        return matchUsersWithChildren();
      default:
        users = [];
    }
    
    const filteredUsers = users.filter(user => {
      if (!user) return false;
      
      if (activeTab === 3) {
        // User matching tab
        return user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               user.parent?.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               user.children?.some(child => 
                 `${child.firstName || ''} ${child.lastName || ''}`.toLowerCase().includes(searchTerm.toLowerCase())
               );
      } else {
        // Other tabs
        return user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               user.lastName?.toLowerCase().includes(searchTerm.toLowerCase());
      }
    });

    return filteredUsers.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Show snackbar
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Close snackbar
  const closeSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  // Handle user row click
  const handleUserClick = (user) => {
    setSelectedUser(user);
    setUserDetailOpen(true);
  };

  // Handle impersonation
  const handleImpersonateClick = (user) => {
    setUserToImpersonate(user);
    setImpersonateDialogOpen(true);
  };

  const handleConfirmImpersonate = async () => {
    if (!userToImpersonate) return;

    try {
      setImpersonating(true);
      const token = await getIdToken();

      // Validate impersonation permission
      const response = await fetch(`${REGISTRATION_BASE_URL}/impersonation/validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          targetUserId: userToImpersonate.uid || userToImpersonate.id,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to validate impersonation');
      }

      const result = await response.json();

      // Start impersonation
      startImpersonation({
        id: result.targetUser.id,
        email: result.targetUser.email,
        displayName: result.targetUser.displayName ||
          `${result.targetUser.firstName || ''} ${result.targetUser.lastName || ''}`.trim(),
      });

      setImpersonateDialogOpen(false);
      setUserToImpersonate(null);

      // Reload the page to refresh all data with the impersonated user's view
      window.location.href = '/';
    } catch (err) {
      console.error('Impersonation failed:', err);
      showSnackbar(`Impersonation failed: ${err.message}`, 'error');
    } finally {
      setImpersonating(false);
    }
  };

  // Check if user can manage admins
  const canManageAdmins = () => isAdmin() || isSuperAdmin();

  if (!isAdmin()) {
    return (
      <Alert severity="warning">
        You don't have permission to access user management.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const sortedUsers = getSortedUsers();

  return (
    <>
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: { xs: 2, sm: 3 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 }
        }}>
          <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 'bold' }}>
            👥 User Management
          </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ImportIcon />}
            onClick={() => setImportDialogOpen(true)}
            size={isMobile ? "small" : "medium"}
            disabled={!currentUser}
            title={!currentUser ? 'Please sign in to import users' : 'Import legacy users from Google Sheets'}
          >
            Import Legacy Users
          </Button>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => {
                setUserToEdit(null);
                setEditUserOpen(true);
              }}
              size={isMobile ? "small" : "medium"}
            >
              Add User
            </Button>
            <IconButton 
              onClick={loadAllData} 
              color="primary" 
              title="Refresh data"
              size={isMobile ? "small" : "medium"}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Tabs */}
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
          sx={{ mb: 3 }}
        >
          <Tab 
            label={`Firebase Users (${firebaseUsers.length})`} 
            icon={<PersonIcon />}
            iconPosition="start"
          />
          <Tab 
            label={`Legacy Users (${legacyUsers.length})`} 
            icon={<ChildIcon />}
            iconPosition="start"
          />
          <Tab 
            label={`Admin Users (${adminUsers.length})`} 
            icon={<AdminIcon />}
            iconPosition="start"
          />
          <Tab 
            label="User Matching" 
            icon={<EmailIcon />}
            iconPosition="start"
          />
        </Tabs>

        {/* Search Bar */}
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={`Search ${activeTab === 0 ? 'Firebase users' : activeTab === 1 ? 'legacy users' : activeTab === 2 ? 'admin users' : 'user matches'}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size={isMobile ? "small" : "medium"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            <Button onClick={loadAllData} sx={{ ml: 2 }} size="small">
              Retry
            </Button>
          </Alert>
        )}

        {/* Content based on active tab */}
        {activeTab === 0 && (
          <FirebaseUsersTable 
            users={sortedUsers}
            onUserClick={handleUserClick}
            onEditUser={(user) => {
              setUserToEdit(user);
              setEditUserOpen(true);
            }}
            onDeleteUser={deleteUser}
            onImpersonate={handleImpersonateClick}
            loading={firebaseUsersLoading}
            isMobile={isMobile}
            currentUser={currentUser}
          />
        )}

        {activeTab === 1 && (
          <LegacyUsersTable 
            users={sortedUsers}
            onUserClick={handleUserClick}
            loading={legacyUsersLoading}
            isMobile={isMobile}
          />
        )}

        {activeTab === 2 && (
          <AdminUsersTable 
            users={sortedUsers}
            onUserClick={handleUserClick}
            onUpdatePermissions={updateUserPermissions}
            canManageAdmins={canManageAdmins()}
            interclubs={interclubs}
            loading={adminUsersLoading}
            isMobile={isMobile}
          />
        )}

        {activeTab === 3 && (
          <UserMatchingTable 
            users={sortedUsers}
            onUserClick={handleUserClick}
            isMobile={isMobile}
          />
        )}

        {sortedUsers.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', py: { xs: 3, sm: 4 } }}>
            <Typography variant={isMobile ? "body2" : "body1"} color="text.secondary">
              {searchTerm ? 'No users found matching your search.' : 'No users found.'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* User Detail Dialog */}
      <UserDetailDialog
        open={userDetailOpen}
        onClose={() => setUserDetailOpen(false)}
        user={selectedUser}
        isMobile={isMobile}
        getIdToken={getIdToken}
      />

      {/* Edit/Create User Dialog */}
      <EditUserDialog
        open={editUserOpen}
        onClose={() => setEditUserOpen(false)}
        user={userToEdit}
        onCreateUser={createFirebaseUser}
        onUpdateUser={updateAdminUser}
        isMobile={isMobile}
        canManageAdmins={canManageAdmins()}
        interclubs={interclubs}
      />

      {/* Import Dialog */}
      <ImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImport={importLegacyUsers}
        progress={importProgress}
        isMobile={isMobile}
        currentUser={currentUser}
      />

      {/* Impersonation Confirmation Dialog */}
      <Dialog
        open={impersonateDialogOpen}
        onClose={() => {
          setImpersonateDialogOpen(false);
          setUserToImpersonate(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ImpersonateIcon color="warning" />
          Impersonate User
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are about to view the application as another user. All actions will be
            logged for auditing purposes.
          </Alert>
          {userToImpersonate && (
            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" color="text.secondary">
                Target User
              </Typography>
              <Typography variant="h6">
                {userToImpersonate.displayName || userToImpersonate.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {userToImpersonate.email}
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setImpersonateDialogOpen(false);
              setUserToImpersonate(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmImpersonate}
            disabled={impersonating}
            startIcon={<ImpersonateIcon />}
          >
            {impersonating ? 'Starting...' : 'Start Impersonation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

// Firebase Users Table Component
function FirebaseUsersTable({
  users,
  onUserClick,
  onEditUser,
  onDeleteUser,
  onImpersonate,
  loading,
  isMobile,
  currentUser,
}) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Don't show impersonate button for self
  const canImpersonate = (user) => currentUser && user.uid !== currentUser.uid;

  // Calculate stats for summary
  const usfsLinkedCount = users.filter(u => u.usfsLinked).length;
  const coachCount = users.filter(u => u.roles?.includes('coach')).length;

  if (isMobile) {
    return (
      <Box>
        {/* Mobile Summary Stats */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            icon={<LinkIcon />}
            label={`${usfsLinkedCount} USFS Linked`}
            color="success"
            variant="outlined"
            size="small"
          />
          <Chip
            icon={<CoachIcon />}
            label={`${coachCount} Coaches`}
            color="primary"
            variant="outlined"
            size="small"
          />
        </Box>

        {users.map((user) => (
          <Paper
            key={user.uid}
            elevation={1}
            sx={{
              mb: 1,
              p: 2,
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'action.hover' }
            }}
            onClick={() => onUserClick(user)}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {user.displayName || user.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.email}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                  {user.roles?.length > 0 && user.roles.map((role) => (
                    <Chip
                      key={role}
                      label={role}
                      size="small"
                      color={role === 'coach' ? 'success' : role.includes('admin') ? 'error' : 'default'}
                      variant="outlined"
                      sx={{ fontSize: '0.65rem', height: 20 }}
                    />
                  ))}
                  {user.usfsLinked && (
                    <Chip
                      icon={<LinkIcon sx={{ fontSize: 12 }} />}
                      label="USFS"
                      size="small"
                      color="success"
                      sx={{ fontSize: '0.65rem', height: 20 }}
                    />
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {canImpersonate(user) && (
                  <IconButton
                    size="small"
                    color="warning"
                    onClick={(e) => {
                      e.stopPropagation();
                      onImpersonate(user);
                    }}
                    title="Impersonate user"
                  >
                    <ImpersonateIcon />
                  </IconButton>
                )}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditUser(user);
                  }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteUser(user);
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }

  return (
    <>
      {/* Summary Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Chip
          icon={<LinkIcon />}
          label={`${usfsLinkedCount} USFS Linked`}
          color="success"
          variant="outlined"
          size="small"
        />
        <Chip
          icon={<CoachIcon />}
          label={`${coachCount} Coaches`}
          color="primary"
          variant="outlined"
          size="small"
        />
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Display Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell>USFS</TableCell>
              <TableCell>Last Sign In</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow 
                key={user.uid} 
                hover 
                onClick={() => onUserClick(user)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {user.displayName || 'Not set'}
                  </Typography>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {user.roles?.length > 0 ? (
                      user.roles.map((role) => (
                        <Chip
                          key={role}
                          label={role}
                          size="small"
                          color={role === 'coach' ? 'success' : role.includes('admin') ? 'error' : 'default'}
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ))
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {user.usfsLinked ? (
                    <Chip
                      icon={<LinkIcon sx={{ fontSize: 14 }} />}
                      label={user.usfsUsername || 'Linked'}
                      size="small"
                      color="success"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  ) : (
                    <Chip
                      icon={<LinkOffIcon sx={{ fontSize: 14 }} />}
                      label="Not Linked"
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', color: 'text.secondary' }}
                    />
                  )}
                </TableCell>
                <TableCell>{formatDate(user.metadata?.lastSignInTime)}</TableCell>
                <TableCell>
                  {canImpersonate(user) && (
                    <IconButton
                      size="small"
                      color="warning"
                      onClick={(e) => {
                        e.stopPropagation();
                        onImpersonate(user);
                      }}
                      title="Impersonate user"
                    >
                      <ImpersonateIcon />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditUser(user);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteUser(user);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

// Legacy Users Table Component
function LegacyUsersTable({ users, onUserClick, loading, isMobile }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isMobile) {
    return (
      <Box>
        {users.map((user, index) => (
          <Paper
            key={user.id}
            elevation={1}
            sx={{
              mb: 1,
              p: 2,
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'action.hover' }
            }}
            onClick={() => onUserClick(user)}
          >
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {user.firstName} {user.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user.email} • {user.club}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Level: {user.startingLevel} → {user.lastLevel || user.startingLevel}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Club</TableCell>
            <TableCell>Level</TableCell>
            <TableCell>Coach</TableCell>
            <TableCell>Registration Date</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <TableRow 
              key={user.id} 
              hover 
              onClick={() => onUserClick(user)}
              sx={{ cursor: 'pointer' }}
            >
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {user.firstName} {user.lastName}
                </Typography>
              </TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>{user.club}</TableCell>
              <TableCell>
                <Chip 
                  label={`${user.startingLevel} → ${user.lastLevel || user.startingLevel}`} 
                  size="small" 
                  color="primary"
                />
              </TableCell>
              <TableCell>{user.coachName || 'Not set'}</TableCell>
              <TableCell>{formatDate(user.timestamp)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// Admin Users Table Component
function AdminUsersTable({ users, onUserClick, onUpdatePermissions, canManageAdmins, interclubs = [], loading, isMobile }) {
  const handlePermissionChange = (userId, permission, checked) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    
    const newPermissions = checked 
      ? [...user.permissions, permission]
      : user.permissions.filter(p => p !== permission);
    
    onUpdatePermissions(userId, newPermissions);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const resolveInterclubNames = (user) => {
    const ids = Array.isArray(user.interclubIds) && user.interclubIds.length > 0
      ? user.interclubIds
      : user.interclubId
        ? [user.interclubId]
        : [];
    if (ids.length === 0) return '—';
    return ids
      .map((id) => interclubs.find((interclub) => interclub.id === id)?.name || id)
      .join(', ');
  };

  if (isMobile) {
    return (
      <Box>
        {users.map((user, index) => (
          <Paper
            key={user.id}
            elevation={1}
            sx={{
              mb: 1,
              p: 2,
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'action.hover' }
            }}
            onClick={() => onUserClick(user)}
          >
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {user.displayName || user.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user.email} • Role: {user.role} • Interclubs: {resolveInterclubNames(user)}
              </Typography>
              <Box sx={{ mt: 1 }}>
                {Object.values(USER_PERMISSIONS).map(permission => (
                  <FormControlLabel
                    key={permission}
                    control={
                      <Switch
                        size="small"
                        checked={user.permissions.includes(permission)}
                        onChange={(e) => handlePermissionChange(user.id, permission, e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    }
                    label={permission.replace('_', ' ')}
                    sx={{ fontSize: '0.75rem' }}
                  />
                ))}
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Interclubs</TableCell>
            <TableCell>Permissions</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <TableRow 
              key={user.id} 
              hover 
              onClick={() => onUserClick(user)}
              sx={{ cursor: 'pointer' }}
            >
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {user.displayName || 'Not set'}
                </Typography>
              </TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>
                <Chip 
                  label={user.role} 
                  size="small" 
                  color={user.role === 'SUPER_ADMIN' ? 'error' : 'primary'}
                />
              </TableCell>
              <TableCell>{resolveInterclubNames(user)}</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {Object.values(USER_PERMISSIONS).map(permission => (
                    <FormControlLabel
                      key={permission}
                      control={
                        <Switch
                          size="small"
                          checked={user.permissions.includes(permission)}
                          onChange={(e) => handlePermissionChange(user.id, permission, e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      }
                      label=""
                      sx={{ m: 0 }}
                    />
                  ))}
                </Box>
              </TableCell>
              <TableCell>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onUserClick(user);
                  }}
                >
                  <VisibilityIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// User Matching Table Component
function UserMatchingTable({ users, onUserClick, isMobile }) {
  if (isMobile) {
    return (
      <Box>
        {users.map((match, index) => (
          <Paper
            key={match.email}
            elevation={1}
            sx={{
              mb: 1,
              p: 2,
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'action.hover' }
            }}
            onClick={() => onUserClick(match)}
          >
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {match.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {match.parent ? '✅ Firebase User' : '❌ No Firebase Account'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Children: {match.children.length}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Parent Email</TableCell>
            <TableCell>Firebase Account</TableCell>
            <TableCell>Children Count</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((match) => (
            <TableRow 
              key={match.email} 
              hover 
              onClick={() => onUserClick(match)}
              sx={{ cursor: 'pointer' }}
            >
              <TableCell>{match.email}</TableCell>
              <TableCell>
                {match.parent ? (
                  <Chip 
                    label={match.parent.displayName || match.parent.email} 
                    size="small" 
                    color="success"
                  />
                ) : (
                  <Chip 
                    label="No Account" 
                    size="small" 
                    color="error"
                  />
                )}
              </TableCell>
              <TableCell>{match.children.length}</TableCell>
              <TableCell>
                <Chip 
                  label={match.parent ? 'Matched' : 'Unmatched'} 
                  size="small" 
                  color={match.parent ? 'success' : 'warning'}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// Helper function for date formatting (handles Firestore timestamps and avoids timezone shift)
function formatDate(dateValue) {
  if (!dateValue) return 'Not set';
  
  // Handle string dates
  if (typeof dateValue === 'string') {
    // YYYY-MM-DD format - parse without timezone conversion
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      const [year, month, day] = dateValue.split('-');
      return new Date(year, month - 1, day).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
    return dateValue;
  }
  
  // Handle Firestore timestamp
  if (dateValue._seconds) {
    const date = new Date(dateValue._seconds * 1000);
    // Use UTC values to avoid timezone shift for date-only values
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
  }
  
  // Fallback
  const date = new Date(dateValue);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// Helper function for date AND time formatting
function formatDateTime(dateString) {
  if (!dateString) return 'Not set';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

// User Detail Dialog Component
function UserDetailDialog({ open, onClose, user, isMobile, getIdToken }) {
  const [linkedSkaters, setLinkedSkaters] = useState([]);
  const [skatersLoading, setSkatersLoading] = useState(false);

  const isFirebaseUser = user?.uid;
  const isLegacyUser = user?.firstName && user?.lastName;
  const isAdminUser = user?.role;
  const isUserMatch = user?.children;

  // Fetch linked skaters when dialog opens with a Firebase user
  useEffect(() => {
    const fetchLinkedSkaters = async () => {
      if (!open || !user?.uid || !getIdToken) {
        setLinkedSkaters([]);
        return;
      }

      try {
        setSkatersLoading(true);
        const token = await getIdToken();
        if (!token) return;

        const response = await fetch(`${REGISTRATION_BASE_URL}/skaters?userId=${user.uid}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const list = data?.skaters ?? (Array.isArray(data) ? data : []);
          setLinkedSkaters(Array.isArray(list) ? list : []);
        } else {
          setLinkedSkaters([]);
        }
      } catch (err) {
        console.error('Error fetching linked skaters:', err);
        setLinkedSkaters([]);
      } finally {
        setSkatersLoading(false);
      }
    };

    if (isFirebaseUser) {
      fetchLinkedSkaters();
    }
  }, [open, user?.uid, isFirebaseUser, getIdToken]);

  if (!user) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth={isMobile ? "xs" : "md"}
      fullScreen={isMobile}
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 2,
          m: { xs: 1, sm: 2 }
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 }
        }}>
          <Typography variant={isMobile ? "h6" : "h6"}>
            👤 {isFirebaseUser ? (user.displayName || user.email) : 
                isLegacyUser ? `${user.firstName} ${user.lastName}` : 
                isAdminUser ? (user.displayName || user.email) :
                user.email}
          </Typography>
          <IconButton 
            onClick={onClose}
            size={isMobile ? "small" : "medium"}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: { xs: 1, sm: 2 } }}>
          {isFirebaseUser && (
            <FirebaseUserDetails 
              user={user} 
              linkedSkaters={linkedSkaters}
              skatersLoading={skatersLoading}
            />
          )}
          
          {isLegacyUser && (
            <LegacyUserDetails user={user} />
          )}
          
          {isAdminUser && (
            <AdminUserDetails user={user} />
          )}
          
          {isUserMatch && (
            <UserMatchDetails user={user} />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Button 
          onClick={onClose}
          size={isMobile ? "small" : "medium"}
          fullWidth={isMobile}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Firebase User Details Component
function FirebaseUserDetails({ user, linkedSkaters, skatersLoading }) {
  return (
    <Box>
      {/* User ID - prominent display for admins */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" color="text.secondary">User ID</Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            fontFamily: 'monospace', 
            fontWeight: 'medium',
            wordBreak: 'break-all'
          }}
        >
          {user.uid}
        </Typography>
      </Paper>

      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
        gap: { xs: 2, sm: 3 }, 
        mb: 3 
      }}>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">Email</Typography>
          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
            {user.email}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">Display Name</Typography>
          <Typography variant="body1">
            {user.displayName || 'Not set'}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">Account Created</Typography>
          <Typography variant="body1">
            {formatDateTime(user.metadata?.creationTime)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">Last Sign In</Typography>
          <Typography variant="body1">
            {formatDateTime(user.metadata?.lastSignInTime)}
          </Typography>
        </Box>
      </Box>
      
      {/* Roles Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary">Roles</Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          {user.roles?.length > 0 ? (
            user.roles.map((role) => (
              <Chip 
                key={role}
                label={role}
                size="small"
                color={role === 'coach' ? 'success' : role.includes('admin') ? 'error' : 'default'}
                variant="outlined"
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">No roles assigned</Typography>
          )}
        </Box>
      </Box>

      {/* USFS Link Status */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary">USFS Account Link</Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          {user.usfsLinked ? (
            <Chip 
              icon={<LinkIcon sx={{ fontSize: 14 }} />}
              label={user.usfsUsername ? `Linked (${user.usfsUsername})` : 'Linked'}
              color="success" 
              size="small"
            />
          ) : (
            <Chip 
              icon={<LinkOffIcon sx={{ fontSize: 14 }} />}
              label="Not Linked"
              variant="outlined" 
              size="small"
              sx={{ color: 'text.secondary' }}
            />
          )}
        </Box>
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary">Account Status</Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Chip 
            label={user.emailVerified ? 'Email Verified' : 'Email Not Verified'} 
            color={user.emailVerified ? 'success' : 'warning'} 
            size="small"
          />
          <Chip 
            label={user.disabled ? 'Disabled' : 'Active'} 
            color={user.disabled ? 'error' : 'success'} 
            size="small"
          />
        </Box>
      </Box>

      {/* Linked Skaters Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Linked Skaters
        </Typography>
        {skatersLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">Loading skaters...</Typography>
          </Box>
        ) : linkedSkaters && linkedSkaters.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {linkedSkaters.map((skater) => (
              <Paper key={skater.id} sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {skater.firstName} {skater.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {skater.club || 'No club'} • Level: {skater.currentLevel || skater.startingLevel || 'Not set'}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Skater ID:
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontFamily: 'monospace',
                        fontSize: '0.7rem',
                        wordBreak: 'break-all'
                      }}
                    >
                      {skater.id}
                    </Typography>
                  </Box>
                </Box>
                {skater.membershipId && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    USFS ID: {skater.membershipId}
                  </Typography>
                )}
              </Paper>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No skaters linked to this account
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// Legacy User Details Component
function LegacyUserDetails({ user }) {
  return (
    <Box>
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
        gap: { xs: 2, sm: 3 }, 
        mb: 3 
      }}>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">Email</Typography>
          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
            {user.email}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">Registration Date</Typography>
          <Typography variant="body1">
            {formatDate(user.timestamp)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
          <Typography variant="body1">{user.phone || 'Not set'}</Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">Date of Birth</Typography>
          <Typography variant="body1">{formatDate(user.dateOfBirth)}</Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">Coach</Typography>
          <Typography variant="body1">{user.coachName || 'Not set'}</Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">Membership ID</Typography>
          <Typography variant="body1">{user.membershipId || 'Not set'}</Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary">Club</Typography>
        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
          {user.club}
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary">Levels</Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Chip label={`Starting: ${user.startingLevel || 'Not Set'}`} color="primary" />
          <Chip 
            label={`Current: ${user.lastLevel || user.startingLevel || 'Not Set'}`} 
            color="secondary"
          />
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary">Competition History</Typography>
        {user.competitions && user.competitions.length > 0 ? (
          <Box sx={{ mt: 1 }}>
            {user.competitions.map((comp, index) => (
              <Chip 
                key={index}
                label={`${comp.name}: ${comp.points} points`}
                sx={{ m: 0.5 }}
                color="default"
                variant="outlined"
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No competitions recorded yet.
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// Admin User Details Component
function AdminUserDetails({ user }) {
  return (
    <Box>
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
        gap: { xs: 2, sm: 3 }, 
        mb: 3 
      }}>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">Email</Typography>
          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
            {user.email}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">Display Name</Typography>
          <Typography variant="body1">
            {user.displayName || 'Not set'}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">Role</Typography>
          <Typography variant="body1">
            <Chip 
              label={user.role} 
              size="small" 
              color={user.role === 'SUPER_ADMIN' ? 'error' : 'primary'}
            />
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">Status</Typography>
          <Typography variant="body1">
            <Chip 
              label={user.isActive ? 'Active' : 'Inactive'} 
              size="small" 
              color={user.isActive ? 'success' : 'error'}
            />
          </Typography>
        </Box>
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary">Permissions</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          {Object.values(USER_PERMISSIONS).map(permission => (
            <Chip
              key={permission}
              label={permission.replace('_', ' ')}
              color={user.permissions.includes(permission) ? 'success' : 'default'}
              variant={user.permissions.includes(permission) ? 'filled' : 'outlined'}
              size="small"
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// User Match Details Component
function UserMatchDetails({ user }) {
  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary">Parent Email</Typography>
        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
          {user.email}
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary">Firebase Account Status</Typography>
        {user.parent ? (
          <Box sx={{ mt: 1 }}>
            <Chip 
              label="✅ Firebase Account Exists" 
              color="success" 
              size="small"
            />
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Name:</strong> {user.parent.displayName || user.parent.email}
            </Typography>
            <Typography variant="body2">
              <strong>Created:</strong> {formatDate(user.parent.metadata?.creationTime)}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 1 }}>
            <Chip 
              label="❌ No Firebase Account" 
              color="error" 
              size="small"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This parent needs to create a Firebase account to access the system.
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Children ({user.children.length})
        </Typography>
        <Box sx={{ mt: 1 }}>
          {user.children.map((child, index) => (
            <Paper key={index} sx={{ p: 2, mb: 1, bgcolor: 'grey.50' }}>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {child.firstName} {child.lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Club: {child.club} • Level: {child.startingLevel} → {child.lastLevel || child.startingLevel}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// Edit User Dialog Component
function EditUserDialog({ open, onClose, user, onCreateUser, onUpdateUser, isMobile, canManageAdmins, interclubs }) {
  const [formData, setFormData] = useState({
    email: '',
    displayName: '',
    role: USER_ROLES.PARENT,
    permissions: [],
    interclubIds: []
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || '',
        displayName: user.displayName || '',
        role: user.role || USER_ROLES.PARENT,
        permissions: user.permissions || [],
        interclubIds: user.interclubIds && user.interclubIds.length > 0
          ? user.interclubIds
          : user.interclubId
            ? [user.interclubId]
            : []
      });
    } else {
      setFormData({
        email: '',
        displayName: '',
        role: USER_ROLES.PARENT,
        permissions: [],
        interclubIds: []
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (user) {
        await onUpdateUser(user.id || user.uid, formData);
      } else {
        await onCreateUser(formData);
      }
      onClose();
    } catch (error) {
      console.error('Error saving user:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (permission, checked) => {
    setFormData(prev => ({
      ...prev,
      permissions: checked 
        ? [...prev.permissions, permission]
        : prev.permissions.filter(p => p !== permission)
    }));
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        {user ? 'Edit User' : 'Create New User'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              required
              fullWidth
            />
            
            <TextField
              label="Display Name"
              value={formData.displayName}
              onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
              fullWidth
            />
            
            {canManageAdmins && (
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  label="Role"
                >
                  <MenuItem value={USER_ROLES.PARENT}>Parent</MenuItem>
                  <MenuItem value={USER_ROLES.ADMIN}>Admin</MenuItem>
                  <MenuItem value={USER_ROLES.INTERCLUB_ADMIN}>Interclub Admin</MenuItem>
                  <MenuItem value={USER_ROLES.SUPER_ADMIN}>Super Admin</MenuItem>
                </Select>
              </FormControl>
            )}

            {formData.role === USER_ROLES.INTERCLUB_ADMIN && (
              <FormControl fullWidth>
                <InputLabel>Interclubs</InputLabel>
                <Select
                  multiple
                  value={formData.interclubIds}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      interclubIds: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value,
                    }))
                  }
                  label="Interclubs"
                  renderValue={(selected) =>
                    selected
                      .map((id) => interclubs.find((interclub) => interclub.id === id)?.name || id)
                      .join(', ')
                  }
                >
                  {interclubs.map((interclub) => (
                    <MenuItem key={interclub.id} value={interclub.id}>
                      <Checkbox checked={formData.interclubIds.includes(interclub.id)} />
                      <ListItemText primary={interclub.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            {formData.role !== USER_ROLES.PARENT && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Permissions
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {Object.values(USER_PERMISSIONS).map(permission => (
                    <FormControlLabel
                      key={permission}
                      control={
                        <Switch
                          checked={formData.permissions.includes(permission)}
                          onChange={(e) => handlePermissionChange(permission, e.target.checked)}
                        />
                      }
                      label={permission.replace('_', ' ')}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
          >
            {loading ? 'Saving...' : (user ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// Import Dialog Component
function ImportDialog({ open, onClose, onImport, progress, isMobile, currentUser }) {
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        Import Legacy Users
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          This will import all users from the Google Sheets registration form into Firestore.
          Users can then sign in (Google OR email/password) and automatically link their accounts to access their data.
        </Typography>
        
        {/* Show current user status */}
        <Alert severity={currentUser ? 'success' : 'warning'} sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Current Status:</strong> {currentUser ? `Signed in as ${currentUser.email}` : 'Not signed in'}
          </Typography>
        </Alert>
        
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>How it works:</strong>
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <li>Users are imported to a "legacy users" collection</li>
            <li>When they sign in (Google OR email/password), their account is automatically linked</li>
            <li>They can access all their competition history and registration data</li>
            <li>Works with both Google Sign-In and Firebase email/password accounts</li>
          </Box>
        </Alert>
        
        {progress > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Import Progress: {progress}%
            </Typography>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={onImport} 
          variant="contained" 
          startIcon={<ImportIcon />}
        >
          Start Import
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Legacy Data Link Component (for regular users)
export function LegacyDataLink() {
  const { user, getIdToken } = useFirebaseAuth();
  const [legacyData, setLegacyData] = useState(null);
  const [hasSkaters, setHasSkaters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showClaimDialog, setShowClaimDialog] = useState(false);
  const [claimForm, setClaimForm] = useState({ membershipId: '', firstName: '', lastName: '' });
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimError, setClaimError] = useState(null);
  const [claimResult, setClaimResult] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const checkLegacyData = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const token = await getIdToken();
      if (!token) {
        throw new Error('Unable to get authentication token');
      }
      
      const [legacyResponse, skaters] = await Promise.all([
        fetch(`${API_BASE_URL}/my-legacy-data`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }),
        registrationService.getSkaters(token)
      ]);

      const hasExistingSkaters = Array.isArray(skaters) && skaters.length > 0;
      const hasVerifiedSkaters = hasExistingSkaters && skaters.some((s) => s.usfsVerified);
      setHasSkaters(hasExistingSkaters || hasVerifiedSkaters);

      if (legacyResponse.ok) {
        const data = await legacyResponse.json();
        setLegacyData(data);
      } else if (legacyResponse.status === 404) {
        setLegacyData({ legacyUser: null, isLinked: false });
      } else {
        throw new Error('Failed to fetch legacy data');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user, getIdToken]);

  const handleSearchLegacy = async () => {
    if (!claimForm.membershipId) {
      setClaimError('Please enter your USFS membership ID');
      return;
    }

    try {
      setClaimLoading(true);
      setClaimError(null);
      setClaimResult(null);

      const token = await getIdToken();
      const response = await fetch(`${API_BASE_URL}/claim-legacy-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(claimForm),
      });

      const data = await response.json();

      if (!response.ok) {
        setClaimError(data.error || 'Failed to find legacy data');
        if (data.expectedName) {
          setClaimError(`${data.error}. Expected: ${data.expectedName}`);
        }
        return;
      }

      setClaimResult(data);
    } catch (err) {
      setClaimError(err.message || 'Failed to search legacy data');
    } finally {
      setClaimLoading(false);
    }
  };

  const handleConfirmLink = async () => {
    try {
      setConfirmLoading(true);
      setClaimError(null);

      const token = await getIdToken();
      const response = await fetch(`${API_BASE_URL}/confirm-legacy-link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ membershipId: claimResult.legacyData.membershipId }),
      });

      const data = await response.json();

      if (!response.ok) {
        setClaimError(data.error || 'Failed to link legacy data');
        return;
      }

      // Success - refresh and close
      setShowClaimDialog(false);
      setClaimResult(null);
      setClaimForm({ membershipId: '', firstName: '', lastName: '' });
      await checkLegacyData();
    } catch (err) {
      setClaimError(err.message || 'Failed to confirm link');
    } finally {
      setConfirmLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      checkLegacyData();
    }
  }, [user, checkLegacyData]);

  if (!user) return null;

  if (loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Checking for legacy data...
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">
          Error loading legacy data: {error}
          <Button onClick={checkLegacyData} sx={{ ml: 2 }} size="small">
            Retry
          </Button>
        </Alert>
      </Paper>
    );
  }

  if (!legacyData) return null;

  if (hasSkaters) {
    return null;
  }

  if (legacyData.legacyUser) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          🎉 Welcome Back!
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Your account is linked to your legacy registration data.
        </Typography>
        
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Name</Typography>
            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
              {legacyData.legacyUser.firstName} {legacyData.legacyUser.lastName}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Club</Typography>
            <Typography variant="body1">
              {legacyData.legacyUser.club}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">USFS ID</Typography>
            <Typography variant="body1">
              {legacyData.legacyUser.membershipId || 'Not provided'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Level</Typography>
            <Typography variant="body1">
              {legacyData.legacyUser.startingLevel} → {legacyData.legacyUser.lastLevel || legacyData.legacyUser.startingLevel}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Competitions</Typography>
            <Typography variant="body1">
              {legacyData.legacyUser.competitions?.length || 0} events
            </Typography>
          </Box>
        </Box>

        <Alert severity="success">
          ✅ Your account is fully linked and you can access all your competition data!
        </Alert>
      </Paper>
    );
  }

  // No email match found - show option to claim by membership ID
  return (
    <>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          🔍 Link Your Skating History
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Have previous competition data from a different email? You can link it using your USFS membership ID.
        </Typography>
        
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Why use your membership ID?</strong> Your USFS membership number uniquely identifies you, 
            so we can verify you're the rightful owner of the skating records even if your email has changed.
          </Typography>
        </Alert>

        <Button variant="outlined" onClick={() => setShowClaimDialog(true)}>
          Claim Legacy Data
        </Button>
      </Paper>

      <Dialog open={showClaimDialog} onClose={() => setShowClaimDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Claim Your Legacy Skating Data</DialogTitle>
        <DialogContent>
          {!claimResult ? (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter your USFS membership ID to search for your skating records. We'll verify your identity
                before linking the data to your account.
              </Typography>

              {claimError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {claimError}
                </Alert>
              )}

              <TextField
                fullWidth
                label="USFS Membership ID"
                value={claimForm.membershipId}
                onChange={(e) => setClaimForm((prev) => ({ ...prev, membershipId: e.target.value }))}
                sx={{ mb: 2 }}
                placeholder="e.g., 1234567"
                required
              />
              <TextField
                fullWidth
                label="First Name (as registered with USFS)"
                value={claimForm.firstName}
                onChange={(e) => setClaimForm((prev) => ({ ...prev, firstName: e.target.value }))}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Last Name (as registered with USFS)"
                value={claimForm.lastName}
                onChange={(e) => setClaimForm((prev) => ({ ...prev, lastName: e.target.value }))}
                sx={{ mb: 2 }}
              />
            </Box>
          ) : (
            <Box sx={{ pt: 2 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Found your legacy skating data! Please confirm this is your record.
              </Alert>

              {claimError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {claimError}
                </Alert>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {claimResult.legacyData.firstName} {claimResult.legacyData.lastName}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">USFS ID</Typography>
                  <Typography variant="body1">
                    {claimResult.legacyData.membershipId}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Club</Typography>
                  <Typography variant="body1">
                    {claimResult.legacyData.club || 'Not specified'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Level</Typography>
                  <Typography variant="body1">
                    {claimResult.legacyData.startingLevel} → {claimResult.legacyData.lastLevel || claimResult.legacyData.startingLevel}
                  </Typography>
                </Box>
              </Box>

              <Alert severity="warning">
                <Typography variant="body2">
                  <strong>Is this your data?</strong> Only confirm if this skating record belongs to you. 
                  Claiming someone else's data is prohibited.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowClaimDialog(false);
            setClaimResult(null);
            setClaimError(null);
            setClaimForm({ membershipId: '', firstName: '', lastName: '' });
          }}>
            Cancel
          </Button>
          {!claimResult ? (
            <Button
              variant="contained"
              onClick={handleSearchLegacy}
              disabled={claimLoading || !claimForm.membershipId}
            >
              {claimLoading ? 'Searching...' : 'Search'}
            </Button>
          ) : (
            <>
              <Button onClick={() => setClaimResult(null)}>
                Try Different ID
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleConfirmLink}
                disabled={confirmLoading}
              >
                {confirmLoading ? 'Linking...' : 'Yes, This Is Me'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
